import {
  putInstructionsBarVisibilityRequest,
  putInstructionsBarVisibilityResponse,
  type PutInstructionsBarVisibilityRequest,
  type PutInstructionsBarVisibilityResponse,
} from '@core-api/editor/editor/slide/{slide_id}/instructions-bar-visibility';
import { questionHideVotingInstructionsBarToInstructionsBarVisibility } from '@mentimeter/compatibility/forward-compatibility/question';
import { useEditorSlideDeckUpdateNotifier } from '@mentimeter/editor-realtime';
import type { Slide } from '@mentimeter/editor-schema/api-types-overrides';
import update from 'immutability-helper';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import useSWRMutation, { type MutationFetcher } from 'swr/mutation';
import { getByCacheKey } from '../../cache/getByCacheKey';
import { getEditorSWRCacheKey } from '../../cache/getEditorSWRCacheKey';
import {
  makeRequest,
  type ApiRequestParams,
} from '../../request-wrapper/make-request';

type MutationArguments = Omit<
  ApiRequestParams<PutInstructionsBarVisibilityRequest>,
  'region'
>;

interface UpdateInstructionsBarVisibilityCallbackArgs {
  slideId: string;
  hideInstructionsBar: boolean;
}

const request: MutationFetcher<
  PutInstructionsBarVisibilityResponse,
  string,
  MutationArguments
> = async (_, { arg: { slideId, instructionsBarVisibility } }) => {
  const fetchResponse = await fetch(
    makeRequest(putInstructionsBarVisibilityRequest, {
      slideId,
      instructionsBarVisibility,
    }),
  );
  const response = await putInstructionsBarVisibilityResponse(fetchResponse, {
    enableStatusCheck: true,
  });
  return response;
};

export const useEditorSlideUpdateInstructionsBarVisibility = (config: {
  seriesId: string;
}) => {
  const { seriesId: slideDeckPublicKey } = config;
  const { cache } = useSWRConfig();

  const requestWithPublish = useEditorSlideDeckUpdateNotifier(
    slideDeckPublicKey,
    request,
  );

  const slidesCacheKey = getEditorSWRCacheKey('slides', {
    slideDeckPublicKey,
  });

  const { trigger } = useSWRMutation<
    PutInstructionsBarVisibilityResponse,
    any,
    string,
    MutationArguments,
    Array<Slide> | undefined
  >(slidesCacheKey, requestWithPublish, {
    populateCache: false,
  });

  return useCallback(
    async (args: UpdateInstructionsBarVisibilityCallbackArgs) => {
      const instructionsBarVisibility =
        questionHideVotingInstructionsBarToInstructionsBarVisibility(
          args.hideInstructionsBar,
        );

      const result = await trigger<Array<Slide>>(
        {
          slideId: args.slideId,
          instructionsBarVisibility,
        },
        {
          revalidate: false,
          optimisticData() {
            const currentData = getByCacheKey<Array<Slide>>(
              cache,
              slidesCacheKey,
            );
            const index =
              currentData?.findIndex((q) => q.slideId === args.slideId) ?? -1;
            if (currentData && index >= 0) {
              const updated = update(currentData, {
                [index]: {
                  instructionsBarVisibility: {
                    $set: instructionsBarVisibility,
                  },
                },
              });
              return updated;
            }

            return currentData ?? [];
          },
        },
      );

      return result;
    },
    [trigger, cache, slidesCacheKey],
  );
};
